import React from 'react';
import { RiLoader4Line } from 'react-icons/ri';
import { useInjectCSS } from './tutorialHelpers';

/**
 * WorkflowBuilderDiagram
 *
 * A static, canvas-style illustration of a workflow pipeline: Input → Tool →
 * Agent → Output nodes wired left to right, with the Agent node shown in a
 * "running" state to hint at the live per-node / per-agent progress streamed
 * into run cards while a workflow executes.
 */

const NODES = [
  { label: 'Input', color: '#0288d1', sub: 'your query' },
  { label: 'Tool', color: '#f59e0b', sub: 'web search' },
  { label: 'Agent', color: '#7b1fa2', sub: 'Researcher', running: true },
  { label: 'Output', color: '#28a745', sub: 'final report' },
];

function NodeBox({ label, color, sub, running }) {
  return (
    <div style={{
      border: `1px solid ${running ? color : 'var(--border-color, #ddd)'}`,
      boxShadow: running ? `0 0 0 2px ${color}22` : 'none',
      borderRadius: '8px',
      padding: '0.45rem 0.6rem',
      minWidth: 92,
      backgroundColor: 'var(--panel-background, #fff)',
      textAlign: 'center',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        fontWeight: 700, color, fontSize: '0.82rem',
      }}>
        {running && (
          <RiLoader4Line size={13} className="sdemo-spin" style={{ flexShrink: 0 }} />
        )}
        {label}
      </div>
      <div style={{
        fontSize: '0.72rem', color: 'var(--text-secondary, #888)', marginTop: 2,
      }}>
        {running ? 'running…' : sub}
      </div>
    </div>
  );
}

const Connector = () => (
  <span style={{
    color: 'var(--text-secondary, #aaa)', fontSize: '1.1rem', padding: '0 2px',
  }}>
    →
  </span>
);

export default function WorkflowBuilderDiagram() {
  useInjectCSS();
  return (
    <div style={{
      border: '1px solid var(--border-color, #e2e2e2)',
      borderRadius: '10px',
      padding: '1rem',
      margin: '1rem 0',
      backgroundColor: 'var(--bg-secondary, #fafafa)',
    }}>
      <div style={{
        fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary, #888)',
        marginBottom: '0.75rem',
      }}>
        A workflow on the canvas
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', gap: '0.4rem',
      }}>
        {NODES.map((n, i) => (
          <React.Fragment key={n.label}>
            <NodeBox {...n} />
            {i < NODES.length - 1 && <Connector />}
          </React.Fragment>
        ))}
      </div>
      <p style={{
        margin: '0.85rem 0 0',
        fontSize: '0.8rem',
        color: 'var(--text-secondary, #777)',
        fontStyle: 'italic',
      }}>
        Drag Input, Agent, Tool, Logic, and Output nodes onto the canvas and
        wire them together. When you run it, each node lights up live as it
        works — including per-agent progress inside a node.
      </p>
    </div>
  );
}
